import { api } from '@/lib/api';
import type { Certificate } from './types';
import type {
  CreateCertificateInput,
  RevokeCertificateInput,
} from './schema';

/**
 * List filters accepted by the parent-scoped endpoints. Backend
 * exposes ?status, ?scope, ?expiresWithinDays, ?includeRevoked. The
 * frontend wires the first three; includeRevoked stays unwired
 * (admin-only audit territory).
 */
export interface CertificateListFilters {
  status?: string;
  scope?: string;
  expiresWithinDays?: number;
}

/**
 * Certificates REST client (Phase 5a).
 *
 * The backend mounts the controller without a single base path —
 * routes are scattered under /companies/:id/..., /vessels/:id/...,
 * /equipment/:id/... etc. so the service exposes one method per
 * route shape rather than a generic list.
 *
 * Endpoints:
 *   GET  /certificates/:id                              detail
 *   GET  /companies/:companyId/certificates             list by company
 *   GET  /vessels/:vesselId/certificates                list by vessel
 *   GET  /equipment/:equipmentId/certificates           list by equipment
 *   GET  /companies/:companyId/certificates/expiring    expiring soon
 *   POST /certificates                                  issue new
 *   POST /certificates/:id/revoke                       revoke (with reason)
 *   DELETE /certificates/:id                            soft-delete (admin)
 */
export const certificatesApi = {
  getById: async (id: string): Promise<Certificate> => {
    const { data } = await api.get<Certificate>(`/certificates/${id}`);
    return data;
  },

  listByCompany: async (
    companyId: string,
    filters: CertificateListFilters = {},
  ): Promise<Certificate[]> => {
    const { data } = await api.get<Certificate[]>(
      `/companies/${companyId}/certificates`,
      { params: filters },
    );
    return data;
  },

  listByVessel: async (
    vesselId: string,
    filters: CertificateListFilters = {},
  ): Promise<Certificate[]> => {
    const { data } = await api.get<Certificate[]>(
      `/vessels/${vesselId}/certificates`,
      { params: filters },
    );
    return data;
  },

  listByEquipment: async (
    equipmentId: string,
    filters: CertificateListFilters = {},
  ): Promise<Certificate[]> => {
    const { data } = await api.get<Certificate[]>(
      `/equipment/${equipmentId}/certificates`,
      { params: filters },
    );
    return data;
  },

  /**
   * Expiring within the company-configured alert window. The backend
   * accepts an optional ?days override; we forward whatever the
   * caller provides.
   */
  listExpiringByCompany: async (
    companyId: string,
    days?: number,
  ): Promise<Certificate[]> => {
    const { data } = await api.get<Certificate[]>(
      `/companies/${companyId}/certificates/expiring`,
      { params: days !== undefined ? { days } : undefined },
    );
    return data;
  },

  create: async (input: CreateCertificateInput): Promise<Certificate> => {
    const { data } = await api.post<Certificate>('/certificates', input);
    return data;
  },

  revoke: async (
    id: string,
    input: RevokeCertificateInput,
  ): Promise<Certificate> => {
    const { data } = await api.post<Certificate>(
      `/certificates/${id}/revoke`,
      input,
    );
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/certificates/${id}`);
  },
};
